import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SharedAutocomplete from '../../../shared/shared-autocomplete';
import DriveGridWithTree from '../../user-management/user-details/drive/drive-grid-with-tree';
import SharedDriveGridWithTree from '../../user-management/user-details/drive/shared-drive-grid-with-tree';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useChangeLogGate } from '../../../shared/hooks/use-change-log-gate';
import { pagesConfig } from '../../../shared/pages-config';
import { Messages } from '../../../utils/message-constants';
import { useUserSettings } from '../../../utils/use-user-settings';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useStore } from '../../state/zustand-store';
import { getDriveRootIdForExplorer } from '../../../api/users-api';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';

export default function DriveExplorerPage() {
  const { usersTier, primaryDomain, driveRoles, changeLogRoles } =
    useUserSettings();
  const { setSnackbar } = useStore();
  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [showSharedDrive, setShowSharedDrive] = useState(false);
  const [clearWasClicked, setClearWasClicked] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const hasUserEmail = searchParams.has('userEmail');
  const userEmail = searchParams.get('userEmail') || '';

  const [initialDriveRootId, setInitialDriveRootId] = useState<string | null>(
    null
  );

  const hasDriveExplorerChangeLogPermission = isAbleToSeeUIElement(
    changeLogRoles,
    DriveRoles.ViewDriveExplorer.value
  );

  const userHasSharedDrivesPermission = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.ViewSharedDrives.value
  );

  const requiresEntryChangeLog =
    hasDriveExplorerChangeLogPermission && hasUserEmail && userEmail !== '';

  const changeLogGate = useChangeLogGate({
    permissionValue: DriveRoles.ViewDriveExplorer.value,
    changeLogRoles,
    initialOpen: requiresEntryChangeLog && !showSharedDrive,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleEntryChangeLogSubmit() {
    if (!userEmail || !changeLogGate.changeLogMessage.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const rootId = await getDriveRootIdForExplorer(
        userEmail,
        changeLogGate.changeLogMessage
      );

      // Validate that the API returned a valid root ID
      if (!rootId) {
        throw new Error('API returned an invalid root ID.');
      }

      setInitialDriveRootId(rootId);
      changeLogGate.confirm();
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to load drive data. Please try again.',
        severity: 'error',
      });
      // Reset to initial state on error
      changeLogGate.reset();
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleAutoCompleteSelection = (value: string) => {
    changeLogGate.reset();
    setShowSharedDrive(false);
    setInitialDriveRootId(null);
    setAutoCompleteValue(value);
    if (value === '') {
      setSearchParams();
    } else {
      setSearchParams({ userEmail: value });
    }

    // Open changelog dialog if required
    if (hasDriveExplorerChangeLogPermission && value) {
      changeLogGate.openDialog();
    }
  };

  const previousUserEmail = useRef(userEmail);

  useEffect(() => {
    if (userEmail !== previousUserEmail.current) {
      previousUserEmail.current = userEmail;
      setAutoCompleteValue(userEmail);
      setShowSharedDrive(false);
      setInitialDriveRootId(null);
      changeLogGate.reset();

      if (hasDriveExplorerChangeLogPermission && userEmail) {
        changeLogGate.openDialog();
      }
    }
  }, [userEmail, hasDriveExplorerChangeLogPermission, changeLogGate]);

  useEffect(() => {
    if (clearWasClicked) {
      setSearchParams();
      setClearWasClicked(false);
      setInitialDriveRootId(null);
      changeLogGate.reset();
    }
  }, [clearWasClicked, setSearchParams]);

  const canViewSharedDrives =
    usersTier.rank >= pagesConfig.sharedDrives.minimumTier;

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[
        { label: ModuleList.modules.drive.name },
        { label: pagesConfig.driveExplorer.name },
      ]}
      bookmarkKey={pagesConfig.driveExplorer.value}
      topContent={
        <Stack flexDirection="row" gap={1} justifyContent="space-between">
          <Box
            sx={{
              width: 400,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <SharedAutocomplete
              sources={['users']}
              autocompleteValue={autoCompleteValue}
              setAutoCompleteValue={setAutoCompleteValue}
              onSelection={handleAutoCompleteSelection}
            />
          </Box>
          <Tooltip
            arrow
            title={
              !canViewSharedDrives
                ? Messages.Errors.Tier.Viewing(
                    pagesConfig.sharedDrives.minimumTierDisplayValue
                  )
                : !hasUserEmail
                ? 'Please enter a user email'
                : !userHasSharedDrivesPermission
                ? Messages.Errors.Permission.Viewing(
                    DriveRoles.ViewSharedDrives.value
                  )
                : ''
            }
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showSharedDrive}
                    onChange={(e) => setShowSharedDrive(e.target.checked)}
                    disabled={
                      !userHasSharedDrivesPermission ||
                      !hasUserEmail ||
                      !canViewSharedDrives
                    }
                  />
                }
                label="Shared Drives"
              />
            </FormGroup>
          </Tooltip>
        </Stack>
      }
      heightOfTopContent={50}
    >
      <>
        <ChangeLogDialog
          open={
            requiresEntryChangeLog &&
            !showSharedDrive &&
            changeLogGate.openChangeLogDialog
          }
          handleClose={changeLogGate.dismiss}
          changeLogMessage={changeLogGate.changeLogMessage}
          setChangeLogMessage={changeLogGate.setChangeLogMessage}
          mutate={handleEntryChangeLogSubmit}
          mutateLoading={isSubmitting}
        />

        {showSharedDrive ? (
          <SharedDriveGridWithTree
            key={userEmail}
            userEmail={userEmail}
            primaryDomain={primaryDomain}
            driveRoles={driveRoles}
            changeLogRoles={changeLogRoles}
          />
        ) : (
          <DriveGridWithTree
            userEmail={
              requiresEntryChangeLog && !changeLogGate.canFetch ? '' : userEmail
            }
            primaryDomain={primaryDomain}
            driveRoles={driveRoles}
            changeLogRoles={changeLogRoles}
            isAllUsers
            initialDriveRootId={
              requiresEntryChangeLog && !changeLogGate.canFetch
                ? null
                : initialDriveRootId
            }
            isExplorer
            showChangeLogOverlay={
              requiresEntryChangeLog &&
              !changeLogGate.canFetch &&
              !showSharedDrive
            }
            isChangeLogConfirmed={changeLogGate.canFetch}
            onOpenChangeLogDialog={changeLogGate.openDialog}
            requiredPermissionName={DriveRoles.ViewDriveExplorer.name}
          />
        )}
      </>
    </SimpleBreadcrumbsPage>
  );
}
